import type { NormalizedAssignment } from '@scale/shared/assignmentJson/legacy/types/NormalizedAssignment';
import {
  AuditResult,
  AuditResultV2,
  AuditResultV2Collection,
  AuditGradeV2,
  FlagAuditResult,
} from '@scale/shared/defaults';
import { PerAnnotationError } from '@scale/shared/types/customerAudit';
import {
  AuditLookup,
  AuditType,
  BenchmarkThresholdAuditMetadata,
  BenchmarkThresholdAttemptAuditMetadata,
  SpeedAuditLookupResponse,
  AuditViewType,
  AuditResultActionType,
  ChatBulkAuditTaskAction,
} from '@scale/shared/types/corp/attemptAudit';
import { ITaskAttemptToReturnJson } from '@scale/shared/types/taskattempt';
import { IndexedInteractionStepResponse, ChatResponseV2 } from '@scale/shared/types/chat';
import { AttemptAuditCriterionScore } from '@scale/shared/types/speedAudit';

export enum SpotterAuditResult {
  Agree = 'Agree',
  Disagree = 'Disagree',
}

export const resultColors: Record<
  AuditResult | AuditResultV2 | 'Skip' | SpotterAuditResult,
  string
> = {
  // [AuditResult.Perfect]: '#227d40',
  [SpotterAuditResult.Agree]: '#227d40',
  // [AuditResult.Good]: '#7ce35d',
  Skip: '#0086d3',
  // [AuditResult.Ok]: '#ffff00',
  // [AuditResult.Bad]: '#f39c12',
  // [AuditResult.Terrible]: '#c0392b',
  [AuditResultV2.Perfect]: '#ffff00',
  [AuditResultV2.Good]: '#7ce35d',
  [AuditResultV2.Ok]: '#ffff00',
  [AuditResultV2.Bad]: '#f39c12',
  [AuditResultV2.Terrible]: '#c0392b',
  [SpotterAuditResult.Disagree]: '#c0392b',
  // treat undecided as skip, but should never happen
  [AuditResultV2.Undecided]: '#0086d3',
};

export const resultV2Colors: Record<AuditResultV2, string> = {
  [AuditResultV2.Perfect]: '#ffff00',
  [AuditResultV2.Good]: '#7ce35d',
  [AuditResultV2.Ok]: '#ffff00',
  [AuditResultV2.Bad]: '#f39c12',
  [AuditResultV2.Terrible]: '#c0392b',
  // treat undecided as ok, but should never happen
  [AuditResultV2.Undecided]: '#ffff00',
};

export const resultColorsV2: Record<AuditResult | AuditResultV2, string[]> = {
  // [AuditResult.Perfect]: ['#F1F8F5', '#367C4F'],
  // [AuditResult.Good]: ['#F1F8F5', '#367C4F'],
  // [AuditResult.Ok]: ['#FFE082', '#374151'],
  // [AuditResult.Bad]: ['#FFEBEE', '#B71C1C'],
  // [AuditResult.Terrible]: ['#FFEBEE', '#B71C1C'],
  [AuditResultV2.Perfect]: ['#F1F8F5', '#367C4F'],
  [AuditResultV2.Good]: ['#F1F8F5', '#367C4F'],
  [AuditResultV2.Ok]: ['#FFE082', '#374151'],
  [AuditResultV2.Bad]: ['#FFEBEE', '#B71C1C'],
  [AuditResultV2.Terrible]: ['#FFEBEE', '#B71C1C'],
  // treat undecided as ok, but should never happen
  [AuditResultV2.Undecided]: ['#FFE082', '#374151'],
};

export const resultIcons: Record<AuditResult | AuditResultV2, string> = {
  // [AuditResult.Perfect]: 'sparkles',
  // [AuditResult.Good]: 'check',
  // [AuditResult.Ok]: 'check',
  // [AuditResult.Bad]: 'times',
  // [AuditResult.Terrible]: 'times',
  [AuditResultV2.Perfect]: 'sparkles',
  [AuditResultV2.Good]: 'check',
  [AuditResultV2.Ok]: 'check',
  [AuditResultV2.Bad]: 'times',
  [AuditResultV2.Terrible]: 'times',
  // treat undecided as ok, but should never happen
  [AuditResultV2.Undecided]: 'check',
};

export const resultShortcuts: Record<AuditResult | AuditResultV2 | 'Skip', string> = {
  // [AuditResult.Perfect]: 'A',
  // [AuditResult.Good]: 'W',
  // [AuditResult.Ok]: 'D',
  // [AuditResult.Bad]: 'R',
  // [AuditResult.Terrible]: 'F',
  Skip: 'S',
  [AuditResultV2.Perfect]: 'A',
  [AuditResultV2.Good]: 'W',
  [AuditResultV2.Ok]: 'D',
  [AuditResultV2.Bad]: 'R',
  [AuditResultV2.Terrible]: 'F',
  // treat undecided as skip, but should never happen
  [AuditResultV2.Undecided]: 'S',
};

/**
 * This is a partial version of SubtaskAssignment that gets loaded
 * upon loading the batch initially
 */
export interface SpeedAuditLookup {
  _id: string;
  auditBatch: string;
  auditLookup: AuditLookup;
  auditType: string;
  auditedBy: string;
  createdAt: string;
  updatedAt: string;
  project?: string;
  result?: AuditResult;
  resultV2?: AuditResultV2Collection;
  flagResult?: FlagAuditResult;
  comments?: string;
  taskInstructionItems?: string[];
  perAnnotationInstructionItems?: PerAnnotationError[];
  auditAction?: AuditResultActionType | ChatBulkAuditTaskAction;
  taskAttempt?: string;
  trainingAttempt?: string;
  newResponse?: Record<string, any>;
}

export interface SpeedAuditResult {
  objectId: string;
  objectType: string;
  attemptAuditId: string;
  result?: AuditResult;
  resultV2?: AuditResultV2Collection;
  flagResult?: FlagAuditResult;
  comments?: string;
  media?: string;
  taskInstructionItems?: string[]; // Instruction Item ID tagged on whole object
  perAnnotationInstructionItems?: PerAnnotationError[]; // Annotation-level Instruction Item IDs
  auditAction?: AuditResultActionType;
  taskAttempt?: string;
  trainingAttempt?: string;
  newResponse?: Record<string, any>;
}

// Map of criterion ID to its answer
export type RubricAnswers = Record<string, AttemptAuditCriterionScore>;

interface SubtaskAssignmentCommon {
  auditLookup: AuditLookup;
  subtask: NormalizedAssignment;
  responses: SpeedAuditLookupResponse[];
  attemptAuditId?: string;
  result?: AuditResult;
  resultV2?: AuditResultV2Collection;
  flagResult?: FlagAuditResult;
  comments?: string;
  taskInstructionItems?: string[];
  perAnnotationInstructionItems?: PerAnnotationError[];
  taskerFeedback?: string;
  auditAction?: string;
  sendToReviewLevel?: string;
  taskerScore?: AuditGradeV2;
  _id?: string;
  edited?: boolean;
  maxScoreDisputed?: boolean;
  outdated?: boolean;
  postFormattedResponse?: Record<string, any>;
  editedStepList?: string[];
  rubricAnswers?: RubricAnswers;
}

export interface AttemptAuditResults {
  auditAction?: string;
  auditLookup: AuditLookup;
  auditedAttempt?: ITaskAttemptToReturnJson;
  resultV2?: AuditResultV2Collection;
  taskId?: string;
  _id?: string;
  ignored?: boolean;
  edited?: boolean;
  sendToReviewLevel?: string;
}

export interface SaveEditRequest {
  reviewer: string; // TODO remove reviewer?
  attemptAuditId: string;
  reviewChanges: IndexedInteractionStepResponse[] | ChatResponseV2;
}

export interface SubtaskAssignment extends SubtaskAssignmentCommon {
  auditedAttempt?: ITaskAttemptToReturnJson;
}

export interface SubtaskAssignmentAbbreviatedResponse extends SubtaskAssignmentCommon {
  attemptTaskerId: string | null;
}

interface BenchmarkThresholdAttemptAudit {
  _id: string;
  metadata?: BenchmarkThresholdAttemptAuditMetadata;
}

export interface AuditBatch {
  _id: string;
  name: string;
  auditType?: AuditType;
  metadata?: BenchmarkThresholdAuditMetadata;
  completed?: boolean;
  auditViewType?: AuditViewType;
  claimTasks?: boolean;
  auditFeedback?: string;
}

export interface BenchmarkThresholdAuditBatch extends AuditBatch {
  attemptAudits: BenchmarkThresholdAttemptAudit[];
}

export interface ChatBulkAuditBatch extends AuditBatch {
  attemptAudits: string[];
  project?: string;
  updatedAt?: string;
  auditedBy?: string;
  auditRubric?: string;
}

export interface AttemptAuditResultTableRow {
  auditBatch: string;
  task: string;
  auditedBy: string;
  result?: AuditResult;
  resultV2?: AuditResultV2Collection;
  lastAttempt: string;
  subtask: string;
  lastAttempter?: {
    email?: string;
  };
}
